import React, { Fragment, useState } from 'react';
import useSWR from 'swr';
import XLSX from 'xlsx';
import SubTabla from '@material-table/core';
import { Container, makeStyles, Paper, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Add as AddIcon, Edit as EditIcon } from '@material-ui/icons';
import { useSnackbar } from 'notistack';
import MaterialTable from '../../components/materialTable';
import { getDisponibilidadLicencias, getPermisosStockLicencias } from '../../services/request/requestLicenciasSoftware';
import DialogoLicencia from './dialogoLicencia';

/**
 * Licencias de software del stock del Gestor de Activos (spec del frontend §4):
 * una fila por licencia con su total, las asignadas (en uso), las reservadas,
 * las pendientes de instalación y las disponibles, con las reservas vigentes
 * en el detalle. Quien tiene gestionarStockLicencias edita cada fila y agrega
 * licencias nuevas. Las reservas y sus personas se gestionan en el SDA.
 *
 * De cada reserva se muestra el solicitante de su SR (D14 del plan maestro):
 * sólo nombre y correo, como los entrega el backend; de los colaboradores,
 * sólo si la reserva tiene persona o está por asignar.
 */

const useStyles = makeStyles((theme) => ({
  paperTitle: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  title: {
    padding: theme.spacing(3, 0, 2, 0),
    textAlign: 'center',
  },
  aviso: {
    margin: theme.spacing(2, 0),
  },
}));

const pesos = (valor) => (valor === null || valor === undefined ? '—' : `$${Math.round(Number(valor)).toLocaleString('es-CL')}`);
const dolares = (valor) => (valor === null || valor === undefined ? '—' : `US$${Number(valor).toLocaleString('es-CL', { maximumFractionDigits: 2 })}`);

/** Vencimiento como fecha sin hora: se muestra en UTC para no correr el día. */
const fecha = (texto) => (texto ? new Date(`${texto.slice(0, 10)}T00:00:00Z`).toLocaleDateString('es-CL', { timeZone: 'UTC' }) : '—');

const ESTADOS = { reservada: 'Reservada', pendiente_instalacion: 'Pendiente de instalación' };

/** Nombre y correo del solicitante de la SR; null si la reserva no lo tiene (las anteriores al 04-11). */
function solicitanteDe(reserva) {
  const s = reserva && reserva.solicitante;
  if (!s || (!s.nombre && !s.email)) return null;
  return { nombre: s.nombre || '', email: s.email || '' };
}

function Solicitante({ reserva }) {
  const s = solicitanteDe(reserva);
  if (!s) return '—';
  return (
    <Fragment>
      {s.nombre}
      {s.nombre && s.email && <br />}
      {s.email && <span style={{ color: '#616161' }}>{s.email}</span>}
    </Fragment>
  );
}

function textoVencimiento(reserva) {
  if (reserva.estado !== 'reservada') return '—';
  const dias = reserva.dias_para_vencer;
  if (dias === null || dias === undefined) return fecha(reserva.vence_en);
  return `${fecha(reserva.vence_en)} (${dias < 0 ? 'vencida' : `en ${dias} días`})`;
}

function colorVencimiento(reserva) {
  const dias = reserva.dias_para_vencer;
  if (reserva.estado !== 'reservada' || dias === null || dias === undefined) return undefined;
  if (dias <= 7) return '#c62828';
  if (dias <= 30) return '#ef6c00';
  return '#2e7d32';
}

/** Excel con dos hojas: el resumen por licencia y sus reservas vigentes. */
function exportarExcel(filas) {
  const resumen = filas.map((f) => ({
    Marca: f.marca,
    Licencia: f.licencias,
    'Id del producto': f.producto_id,
    Total: f.cant_licencias,
    'Asignadas (en uso)': f.en_uso,
    Reservadas: f.reservadas,
    'Reservadas sin persona': f.reservadas_sin_persona,
    'Pendientes de instalación': f.pendientes_instalacion,
    Disponibles: f.disponibles,
    'Precio mensual CLP': f.precios_clp,
    'Precio mensual USD': f.precios_usd,
    'Costo mensual reservado CLP': f.costo_mensual_reservado_clp,
    'En uso actualizado': f.en_uso_actualizado_en ? new Date(f.en_uso_actualizado_en).toLocaleString('es-CL') : '',
  }));
  const reservas = filas.flatMap((f) =>
    (f.reservas || []).map((r) => {
      const solicitante = solicitanteDe(r);
      return {
        Marca: f.marca,
        Licencia: f.licencias,
        Contrato: r.contrato_codigo,
        'Solicitud (SR)': r.sr,
        Solicitante: solicitante ? solicitante.nombre : '—',
        'Correo del solicitante': solicitante ? solicitante.email : '',
        Persona: r.persona_ref ? 'Asignada' : 'Por asignar',
        Estado: ESTADOS[r.estado] || r.estado,
        Prórrogas: r.prorrogas,
        'Vence la reserva': r.estado === 'reservada' ? r.vence_en : '',
        'Días para vencer': r.estado === 'reservada' ? r.dias_para_vencer : '',
      };
    }),
  );
  const libro = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(libro, XLSX.utils.json_to_sheet(resumen), 'Licencias');
  XLSX.utils.book_append_sheet(libro, XLSX.utils.json_to_sheet(reservas.length ? reservas : [{ Reservas: 'Sin reservas vigentes' }]), 'Reservas');
  XLSX.writeFile(libro, `licencias-software-${new Date().toISOString().slice(0, 10)}.xlsx`);
}

export default function TablaLicencias() {
  const classes = useStyles();
  const notistack = useSnackbar();
  const { data, error, mutate } = useSWR('get:licencias:disponibilidad', getDisponibilidadLicencias);
  const { data: permisosStock } = useSWR('get:licencias:stock:permisos', getPermisosStockLicencias);
  const puedeGestionar = !!(permisosStock && permisosStock.gestionarStockLicencias);
  // undefined: cerrado; null: licencia nueva; una fila: edición.
  const [enEdicion, setEnEdicion] = useState(undefined);
  const filas = Array.isArray(data) ? data : [];

  const columnas = [
    { title: 'Marca', field: 'marca', defaultSort: 'asc' },
    { title: 'Licencia', field: 'licencias' },
    { title: 'Total', field: 'cant_licencias', type: 'numeric' },
    { title: 'Asignadas', field: 'en_uso', type: 'numeric' },
    { title: 'Reservadas', field: 'reservadas', type: 'numeric' },
    { title: 'Pendientes de instalación', field: 'pendientes_instalacion', type: 'numeric' },
    {
      title: 'Disponibles',
      field: 'disponibles',
      type: 'numeric',
      render: (fila) => <b style={{ color: fila.disponibles > 0 ? undefined : '#c62828' }}>{fila.disponibles}</b>,
    },
    { title: 'Precio mensual', field: 'precios_clp', type: 'numeric', render: (fila) => (fila.precios_clp !== null ? pesos(fila.precios_clp) : dolares(fila.precios_usd)) },
    { title: 'Costo mensual reservado', field: 'costo_mensual_reservado_clp', type: 'numeric', render: (fila) => pesos(fila.costo_mensual_reservado_clp) },
    {
      title: 'En uso al',
      field: 'en_uso_actualizado_en',
      render: (fila) => (fila.en_uso_actualizado_en ? new Date(fila.en_uso_actualizado_en).toLocaleDateString('es-CL') : '—'),
    },
  ];

  const acciones = puedeGestionar
    ? [
        { icon: () => <EditIcon />, tooltip: 'Editar licencia', onClick: (_, fila) => setEnEdicion(fila) },
        { icon: () => <AddIcon />, tooltip: 'Nueva licencia', isFreeAction: true, onClick: () => setEnEdicion(null) },
      ]
    : [];

  return (
    <Fragment>
      <Paper className={classes.paperTitle}>
        <Container>
          <Typography variant="h4" className={classes.title}>
            Licencias de software
          </Typography>
        </Container>
      </Paper>
      {error && (
        <Alert severity="error" className={classes.aviso}>
          {(error.response && error.response.data && error.response.data.mensaje) || 'No fue posible cargar las licencias.'}
        </Alert>
      )}
      <MaterialTable
        title=""
        is_loading={!data && !error}
        lockheaders={true}
        columns={columnas}
        data={filas}
        actions={acciones}
        searching={true}
        columnsButton={false}
        grouping={false}
        filtering={false}
        showTitle={false}
        exportButton={true}
        export_function={() => exportarExcel(filas)}
        options={{
          padding: 'default',
          showTitle: false,
          actionsColumnIndex: -1,
          headerStyle: { fontWeight: 'bold', color: '#731f1f', textAlign: 'left', fontSize: '15px' },
        }}
        detailPanel={(fila) => (
          <SubTabla
            title={`Reservas vigentes de ${fila.marca} · ${fila.licencias}`}
            options={{ padding: 'dense', paging: false, search: false, headerStyle: { fontWeight: 'bold' } }}
            localization={{ body: { emptyDataSourceMessage: 'Sin reservas vigentes' } }}
            columns={[
              { title: 'Contrato', field: 'contrato_codigo' },
              { title: 'Solicitud (SR)', field: 'sr' },
              {
                title: 'Solicitante',
                field: 'solicitante.nombre',
                render: (r) => <Solicitante reserva={r} />,
              },
              { title: 'Persona', field: 'persona_ref', render: (r) => (r.persona_ref ? 'Asignada' : 'Por asignar') },
              { title: 'Estado', field: 'estado', render: (r) => ESTADOS[r.estado] || r.estado },
              { title: 'Prórrogas', field: 'prorrogas', type: 'numeric' },
              {
                title: 'Vence la reserva',
                field: 'vence_en',
                render: (r) => <span style={{ color: colorVencimiento(r) }}>{textoVencimiento(r)}</span>,
              },
            ]}
            data={fila.reservas || []}
          />
        )}
      />
      <DialogoLicencia
        open={enEdicion !== undefined}
        fila={enEdicion || null}
        onClose={() => setEnEdicion(undefined)}
        onGuardado={(resultado) => {
          setEnEdicion(undefined);
          mutate();
          notistack.enqueueSnackbar(resultado && resultado.sin_cambios ? 'Sin cambios que guardar' : 'Licencia guardada', {
            variant: 'success',
            anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
          });
        }}
      />
    </Fragment>
  );
}
